import { useNavigate } from "react-router-dom";
import { IFacturaCompleta } from "../facturarCita/DetalleFacturaComplementaria";
import { useEffect, useState } from "react";
import IAlert from "../../../types/Alert";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Snackbar, Switch, TextField, Typography } from "@mui/material";
import IPaciente from "../../../types/Paciente";
import * as constantes from "../../../common/Constantes";
import PacientesUtils from "../../../utils/Pacientes";
import { IFacturaClaseErrors } from "./FacturaClaseForm";
import moment from "moment";
import ConfigUtils from "../../../utils/Config";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import FacturacionService from "../../../services/FacturacionService";
import ILog from "../../../types/Log";
import LogService from "../../../services/LogService";
import { EstadoFacturacionClase } from "./FacturacionMes";
import IServiceClassInvoice from "../../../types/ServiceClassInvoice";
import { getCurrencySymbol, formatNumeroFactura } from "../../../utils/Factura";

interface FacturaClaseFormProps {
  open: boolean;
  onClose: () => void;
  factura?: IFacturaCompleta;
  paciente: IPaciente;
  onSuccess?: (pacienteId: string, actividadId: number, 
  data: {
    result: IServiceClassInvoice;
    factura: IFacturaCompleta;
    estado: EstadoFacturacionClase;
    motivo: string;
  }) => void;
  facturableProp: boolean;
  motivoNoFacturable?: string;
  actividad: string;
  periodo: string;
}

const EditarFacturaClaseForm = ({ open, onClose, factura, paciente, onSuccess, facturableProp, motivoNoFacturable, actividad, periodo }: FacturaClaseFormProps) => {
    const simboloMoneda = getCurrencySymbol(factura?.moneda);
    const history = useNavigate();
    const [alert, setAlert] = useState<IAlert>({ open: false, severity: "info", msg: "" });
    const handleCloseAlert = () => setAlert({ open: false, severity: "info", msg: "" });
    const [loading, setLoading] = useState(false);
    const [showMissingAlert, setShowMissingAlert] = useState(true);

    const [errors, setErrors] = useState<IFacturaClaseErrors>({});
    const [confirmNoFacturable, setConfirmNoFacturable] = useState(false);
    const [motivo, setMotivo] = useState(motivoNoFacturable);
    const [facturable, setFacturable] = useState<boolean>(facturableProp);
    const [nombre, setNombre] = useState(factura?.servicio ?? "");
    const [concepto, setConcepto] = useState(factura?.conceptos[0].nombre ?? "");
    const [precio, setPrecio] = useState<number>(factura?.conceptos[0].precio ?? 0);
    const [precioTotal, setPrecioTotal] = useState<number>(() => {
      const precio = factura?.conceptos?.[0]?.precio ?? 0;
      const impuesto = factura?.impuestos?.[0]?.cuota ?? 0;
      return precio + (precio * impuesto) / 100;
    });
    const [inputPrecioBase, setInputPrecioBase] = useState(factura?.conceptos[0].precio.toFixed(2).replace(".", ",") ?? "0,00");
    const [impuesto, setImpuesto] = useState<number>(factura?.impuestos[0].cuota ?? 0);
    const [facturaPagada, setFacturaPagada] = useState<boolean>(false);
    const [metodoPago, setMetodoPago] = useState(factura?.metodoPago);

    const metodosPago = ConfigUtils.getMetodosPago();
    const actividades = ConfigUtils.getActividades();

    useEffect(() => {
        if (factura && factura.metodoPago) {
          setFacturaPagada(true);
          const metodoPagoResponse = factura.metodoPago;
          setMetodoPago(
            metodoPagoResponse
              ? {
                  ...metodoPagoResponse,
                  fechaPago: moment(metodoPagoResponse.fechaPago),
                }
              : undefined
          );
        }
    }, [factura?.metodoPago])

    const edad = paciente.fechaNacimiento
      ? PacientesUtils.calcularEdad(paciente.fechaNacimiento)
      : null;
    const esMenor = edad !== null && edad !== undefined && edad < 18;
    const missingFields = [
        !paciente.domicilio ? "Dirección" : null,
        !paciente.fechaNacimiento ? "Fecha de Nacimiento" : null,

        esMenor
          ? !paciente.DNItutor
            ? "DNI del tutor"
            : null
          : !paciente.DNI
            ? "DNI"
            : null,
    ].filter((f): f is string => Boolean(f));

    const validateFactura = () => {
      const facturaErrors: IFacturaClaseErrors = {};
      if (!nombre.trim()) facturaErrors.nombre = "El nombre del servicio es obligatorio";
      if (!concepto.trim()) facturaErrors.concepto = "El concepto es obligatorio";
      if (facturable && precio <= 0) facturaErrors.precio = "El precio debe ser mayor que 0 si es facturable";
      if (!facturable && precio < 0) facturaErrors.precio = "El precio no puede ser negativo";
      if (facturaPagada) {
        if (!metodoPago) {
          facturaErrors.metodoPago = "Debes seleccionar un método de pago";
        } else {
          if (!metodoPago.fechaPago) {
            facturaErrors.fechaPago = "Debes seleccionar una fecha de pago";
          } else if (
            !moment.isMoment(metodoPago.fechaPago) ||
            !metodoPago.fechaPago.isValid()
          ) {
            facturaErrors.fechaPago = "Debes seleccionar una fecha de pago válida";
          }
        }
      }
      return facturaErrors;
    }

    const handleEditFactura = async () => {
        const validationErrors = facturable ? validateFactura() : {};
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) return;

        try {
        if (!factura?._id) return;
          setLoading(true);
          const response = await FacturacionService.editFacturaClase( factura._id, {
            pacienteId: paciente._id,
            facturable,
            motivoNoFacturable: facturable ? undefined : motivo,
            servicio: nombre,
            concepto,
            precio: Number(precio.toFixed(2)),
            impuesto,
            metodoPago
          });

          setAlert({
            open: true,
            severity: "success",
            msg: facturable ? "Factura actualizada correctamente" : "Configuración del mes guardada correctamnete"
          });

          onSuccess?.(paciente._id, Number(actividades.find(a => a.title === actividad)?.internalID), response.data);
          onClose();
      
        } catch (e: any) {
          if (e.response?.status === 401) {
            localStorage.clear();
            history(constantes.R_HOME);
          } else {
            const error: ILog = {
              method: "EditarFacturaClaseForm.Component.handleEditFactura",
              level: "error",
              message: e.message,
              meta: e.response,
            };
            LogService.logError(error);
            setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
          }
        } finally {
          setLoading(false);
        }
    };

    return (
        <>
        <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
            <Box
              sx={{
                p: 2,
                backgroundColor: "#f8f9fa",
                border: "1px solid #e5e5e5",
              }}
            >

            {/* Aviso de datos incompletos */}
            {missingFields.length > 0 && showMissingAlert && (
                <Alert
                  severity="warning"
                  sx={{ mb: 1, fontSize: "0.875rem" }}
                  onClose={() => setShowMissingAlert(false)}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">
                      Faltan datos importantes: <b>{missingFields.join(", ")}</b>.
                    </Typography>
                
                    <Button
                      size="small"
                      color="warning"
                      onClick={() => history(constantes.R_NUEVOPACIENTE, { state: paciente })}
                    >
                      Editar
                    </Button>
                  </Box>
                </Alert>
            )}

            <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between">
                {/* Datos del receptor */}
                <Box flex={1}>
                  <Typography variant="h6" gutterBottom>
                    Receptor
                  </Typography>

                  {paciente.DNItutor ? (
                    <>
                      <Typography variant="body2">
                        <b>Paciente:</b> {paciente.nombre} {paciente.apellidos}
                      </Typography>
                      <Typography variant="body2">
                        <b>Nombre:</b> {paciente.nombreTutor} {paciente.apellidosTutor}
                      </Typography>
                      <Typography variant="body2">
                        <b>DNI:</b> {paciente.DNItutor}
                      </Typography>
                    </>
                    ) : (
                    <>
                      <Typography variant="body2">
                        <b>Nombre:</b> {paciente.nombre} {paciente.apellidos}
                      </Typography>            
                      {paciente.DNI && (
                        <Typography variant="body2">
                          <b>DNI:</b> {paciente.DNI}
                        </Typography>
                      )}          
                      {paciente.email && (
                        <Typography variant="body2">
                          <b>Email:</b> {paciente.email}
                        </Typography>
                      )}
                    </>
                    )}

                  {
                    paciente.domicilio && (
                    <Typography variant="body2">
                      <b>Domicilio:</b> {paciente.domicilio}
                    </Typography>
                    )
                  }
                </Box>

                <Box flexShrink={0} mt={{ xs: 2, sm: 0 }}>
                  <Typography variant="h6" gutterBottom>
                    {actividad}
                  </Typography>

                  <Typography variant="body2">
                    {periodo}
                  </Typography>
                </Box>
            </Box> 
            
            </Box>

            <DialogContent>
                {factura && <Box display="flex" flexWrap="wrap" gap={{ xs: 2, md: 4 }} alignItems="center">
                  {/* Número */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Nº:</Typography>
                    {<Typography variant="body2">{formatNumeroFactura(factura.numero)}</Typography>}
                  </Box>
                  {/* Fecha */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Fecha factura:</Typography>
                    <Typography variant="body2">
                      {new Date(factura.fechaFactura).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>}

                {!factura?.facturaOriginalId &&
                <FormControlLabel
                  control={
                    <Switch
                      checked={facturable}
                      onChange={(e) => {
                        if (metodoPago) {
                          setAlert({ open: true, severity: "warning", msg: "No se puede marcar como no facturable: ya está pagada" })
                          return;
                        }
                        const value = e.target.checked;
                        if (!value && facturable) {
                          setConfirmNoFacturable(true);
                          return;
                        }
                        setFacturable(value);
                      }}
                    />
                  }
                  label="Facturable"
                  sx={{ mb: 2 }}
                />}

                {facturable &&
                <>
                  <Grid container spacing={{ xs: 0, md: 2 }}>
                    {/* Servicio */}
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Servicio*"
                        disabled={loading}
                        fullWidth
                        sx={{ mb: 2 }}
                        value={nombre}
                        onChange={(e) => {
                          setNombre(e.target.value);
                          setErrors((prev: IFacturaClaseErrors) => ({ ...prev, nombre: undefined }));
                        }}
                        error={!!errors.nombre}
                        helperText={errors.nombre}
                      />
                    </Grid>
                    {/* Concepto */}
                    <Grid item xs={12} md={8}>
                      <TextField
                        label="Concepto*"
                        disabled={loading}
                        fullWidth
                        sx={{ mb: 2 }}
                        value={concepto}
                        onChange={(e) => {
                          setConcepto(e.target.value);
                          setErrors((prev: IFacturaClaseErrors) => ({ ...prev, concepto: undefined }));
                        }}
                        error={!!errors.concepto}
                        helperText={errors.concepto}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={{ xs: 0, md: 2 }}>
                    {/* Precio */}
                    <Grid item xs={12} md={4}>
                      <TextField
                        label={`Precio base (${simboloMoneda})*`}
                        disabled={loading}
                        fullWidth
                        type="text"
                        sx={{ mb: 2 }}
                        value={inputPrecioBase}
                        onChange={(e) => {
                          let raw = e.target.value;
                          setInputPrecioBase(raw);

                          const normalized = raw.replace(",", ".");
                          if (/^\d*([.,]\d*)?$/.test(raw)) {
                            const nuevoPrecio = parseFloat(normalized);
                          
                            if (!isNaN(nuevoPrecio)) {
                              setPrecio(nuevoPrecio);
                              setPrecioTotal(nuevoPrecio + (nuevoPrecio * impuesto) / 100);
                            } else {
                              setPrecio(0);
                              setPrecioTotal(0);
                            }
                          }
                        }}
                        onBlur={() => {
                          const formatted = precio.toFixed(2).replace(".", ",");
                          setInputPrecioBase(formatted);
                        }}
                        error={!!errors.precio}
                        helperText={errors.precio}
                      />
                    </Grid>
                    {/* Impuesto */}
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="select-impuesto-label">Impuesto (%)*</InputLabel>
                        <Select
                          labelId="select-impuesto-label"
                          disabled={loading}
                          value={impuesto}
                          label="Impuesto (%)*"
                          onChange={(e) => {
                            const nuevoImp = Number(e.target.value);
                            setImpuesto(nuevoImp);
                            setPrecioTotal(precio + (precio * nuevoImp) / 100);
                          }}
                        >
                          {constantes.IMPUESTOS.map((imp) => (
                            <MenuItem key={imp} value={imp}>
                              {imp}%
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {/* Precio total */}
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label={`Precio total (${simboloMoneda})`}
                        value={precioTotal.toFixed(2).replace(".", ",")}
                        InputProps={{ readOnly: true, sx: {
                          backgroundColor: '#f0f0f0',
                          color: '#555',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#bdbdbd'
                          }
                        }}}
                      />
                    </Grid>
                  </Grid>

                    {/* Bloque de Pago */}
                    <Box
                      display="flex"
                      flexWrap="wrap"
                      alignItems="center"
                    >
                      {/* Texto explicativo */}
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ flexBasis: "100%", flexGrow: 1 }}
                      >
                        * Si la factura se marca como pagada, el método de pago es obligatorio.
                      </Typography>
                                    
                      {/* Switch de factura pagada */}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={facturaPagada}
                            disabled={loading}
                            onChange={(e) => {
                              const pagada = e.target.checked;
                                                        
                              setFacturaPagada(pagada);
                              setMetodoPago(undefined);
                            
                              setErrors((prev: IFacturaClaseErrors) => ({
                                ...prev,
                                metodoPago: undefined,
                              }));
                            }}
                          />
                        }
                        label="Factura pagada"
                        sx={{ m: 0, mb: facturaPagada ? 0 : { xs: 0, md: 7 } }}
                      />
                    
                      {/* Select de método de pago */}
                      {facturaPagada && (
                        <Grid container spacing={2} alignItems="flex-start">
                          {/* Método de pago */}
                          <Grid item xs={12} sm={6} md={6}>
                            <FormControl fullWidth 
                            error={!!errors.metodoPago}
                            >
                              <InputLabel id="metodo-pago-label">Método de pago*</InputLabel>
                              <Select
                                labelId="metodo-pago-label"
                                disabled={loading}
                                value={metodoPago?.nombre || ""}
                                label="Método de pago*"
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const metodo = metodosPago.find((m) => m.nombre === value)!;
                                    setMetodoPago({
                                      nombre: metodo.nombre,
                                      codigo: metodo.codigo,
                                      fechaPago: metodoPago?.fechaPago || moment(),
                                    });
                                
                                  setErrors((prev: IFacturaClaseErrors) => ({ ...prev, metodoPago: undefined }));
                                }}
                              >
                                {metodosPago.filter((m) => m.activo).map((m) => (
                                  <MenuItem key={m.codigo} value={m.nombre}>
                                    {m.nombre}
                                  </MenuItem>
                                ))}
                              </Select>
                              {errors.metodoPago && (
                                <Typography color="error" variant="caption">
                                  {errors.metodoPago}
                                </Typography>
                              )}
                            </FormControl>
                          </Grid>
                          
                          {/* Fecha de pago */}
                          <Grid item xs={12} sm={6} md={6}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                              <DatePicker
                                label="Fecha de pago"
                                value={metodoPago?.fechaPago}
                                disabled={loading}
                                onChange={(date) => {
                                  if (metodoPago) {
                                    setMetodoPago({ ...metodoPago, fechaPago: date ?? moment() });
                                    setErrors((prev: IFacturaClaseErrors) => ({ ...prev, fechaPago: undefined }));
                                  }
                                }}
                                renderInput={(params) => ( <TextField {...params} required fullWidth error={!!errors.fechaPago} helperText={errors.fechaPago} 
                                  /> )}
                              />
                            </LocalizationProvider>
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                </>}

                {!facturable &&
                <TextField
                  label="Motivo no facturable"
                  disabled={loading}
                  fullWidth
                  multiline
                  minRows={2}
                  sx={{ mb: 2 }}
                  InputLabelProps={{ shrink: true }}
                  value={motivo ?? ""}
                  onChange={(e) => {
                    setMotivo(e.target.value);
                  }}
                />}

            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                  Cerrar
                </Button>
                <Button onClick={handleEditFactura} variant="contained" color="primary" size="small" disabled={loading}>
                  Editar
                </Button>
            </DialogActions>
        </Dialog>

        {/* CONFIRMAR NO FACTURABLE */}
        <Dialog open={confirmNoFacturable} maxWidth="sm" onClose={() => setConfirmNoFacturable(false)}>
          <DialogContent>
            <Typography>
              ¿Seguro que quieres marcar este pacientes este mes como <b>NO facturable</b>? No aparecerá para facturación.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setFacturable(false);
                setConfirmNoFacturable(false);
                setErrors({});
              }}
            >
              Sí, continuar
            </Button>
            <Button onClick={() => setConfirmNoFacturable(false)}>Cancelar</Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
          <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
            {alert.msg}
          </Alert>
        </Snackbar>

        </>
    );
};

export default EditarFacturaClaseForm;